import moment from "moment-timezone";
import i18n from "src/i18n";

/**
 * Check if param is date
 * @param {string} date - Date to be parsed
 * @returns {boolean} - true, if param is date
 */
export const isDate = (date) => {
    const isDate = Date.parse(date);

    return !isNaN(isDate);
};

/**
 * Format date using moment
 * @param {*} dateString - Date
 * @returns {string} Date string
 */
export const dateFormatterMoment = (dateString) => {
    let date = "N/A";
    try {
        if (dateString) {
            moment.locale("en");
            // Source: https://github.com/moment/moment/issues/1407
            date = moment(new Date(dateString)).format(`MMMM DD, YYYY [${i18n.t("at")}] LT`);
        }
    } catch (e) {
        date = dateString;
    }

    return date;
};

/**
 * Compare cycle start and end date, start should be before end date
 * @param {*} cycleLabel1 - Start date
 * @param {*} cycleLabel2 - End date
 * @returns {*} - Date which is later among the 2 dates provided
 */
export const compareCyclePeriodDate = (cycleLabel1, cycleLabel2) => {
    if (cycleLabel1 === null) {
        return cycleLabel2;
    }

    if (cycleLabel2 === null) {
        return cycleLabel1;
    }

    if (typeof cycleLabel1 === "object") {
        cycleLabel1 = cycleLabel1.label;
    }

    if (typeof cycleLabel2 === "object") {
        cycleLabel2 = cycleLabel2.label;
    }
    let cycleLabel1Date = new Date(cycleLabel1);
    let cycleLabel2Date = new Date(cycleLabel2);
    if (isNaN(cycleLabel1Date)) {
        cycleLabel1 = dateTime(cycleLabel1);
        cycleLabel1Date = new Date(cycleLabel1);
    }

    if (isNaN(cycleLabel2Date)) {
        cycleLabel2 = dateTime(cycleLabel2);
        cycleLabel2Date = new Date(cycleLabel2);
    }

    if (moment(cycleLabel1Date).isAfter(cycleLabel2Date)) {
        return cycleLabel1;
    } else {
        return cycleLabel2;
    }
};

/**
 * Convert date to year week format
 * @param {Date} dateTime - Date time
 * @returns {string} Date in YYYY:W format
 */
export const dateTimeToYearWeek = (dateTime) => {
    return moment(dateTime).format("YYYY:W");
};

/**
 * Convert date to custom format as per aggregateBy
 * @param {Date} dateTime - Date to be converted
 * @param {string} aggregateBy - Y/ME/MS/W/D/H
 * @returns {string} Formatted date
 */
export const dateTimeToCustom = (dateTime, aggregateBy) => {
    let format = "";
    switch (aggregateBy) {
        case "Y":
            format = "YYYY";
            break;
        case "ME":
        case "MS":
            format = "YYYY-MM";
            break;
        case "W":
            format = "YYYY:W";
            break;
        case "D":
            format = "YYYY-MM-DD";
            break;
        case "H":
            format = "YYYY-MM-DD HH";
            break;
        default:
            format = "YYYY:W";
            break;
    }

    return moment.utc(dateTime).format(format);
};

/**
 * Convert date to full format
 * @param {Date} date - Date object
 * @returns {string} - Date in long format
 */
export const dateTimeUtc = (date) => {
    return moment(date, "DD-MM-YYYY").format("MMMM Do YYYY, h:mm:ss a");
};

/**
 * Convert date to particular format
 * @param {Date} date - Date object
 * @param {string} requiredFormat - Required format
 * @returns {string} - Date in required format
 */
export const dateTime = (date, requiredFormat = "DD-MM-YYYY") => {
    return moment.utc(date).format(requiredFormat);
};

/**
 * Convert date time string to ISO format
 * @param {string} date - Date string in given format
 * @param {string} format - Required format
 * @returns {string} - Date in long format
 */
export const dateTimeToIsoTimeZone = (date, format = "DD-MM-YYYY") => {
    return moment(date, format).format("YYYY-MM-DDTHH:mm:ss.sssZ");
};

/**
 * Difference between 2 dates
 * @param {string} dateTime1 - Date 1
 * @param {string} dateTime2 - Date 2
 * @returns {number} Difference in milliseconds
 */
export const timeDifference = (dateTime1, dateTime2) => {
    if (dateTime1 === null || dateTime2 === null) {
        return 0;
    }
    const dateTime1Date = new Date(dateTime1);
    const dateTime2Date = new Date(dateTime2);

    return dateTime1Date - dateTime2Date;
};

/**
 * To get latest date from array of object
 * @param {Array} arrayOfDates - => should be in the format of array of object
 * @param {string} dateField - => is the date field name that to be check in arrayOfDates
 * @returns {Date} Latest date out of list
 */
export const latestDate = (arrayOfDates, dateField) => {
    return Math.max(...arrayOfDates.map((eachDate) => new Date(eachDate[dateField])));
};

/**
 * Date String to Unix
 * @param {string} dateString - Date
 * @returns {number} Unix Date Time
 */
export const dateTimeUnix = (dateString) => {
    return moment(dateString).unix();
};

/**
 * Date Comparator for AG-Grid.
 * @param {string} dateOne - Date
 * @param {string} dateTwo - Date
 * @returns {number} - Difference in dates
 */
export const dateComparator = (dateOne, dateTwo) => {
    if (!dateOne && !dateTwo) {
        return 0;
    }

    if (!dateOne) {
        return -1;
    }

    if (!dateTwo) {
        return 1;
    }

    const dateOneNumber = dateTimeUnix(dateOne);
    const dateTwoNumber = dateTimeUnix(dateTwo);

    return dateOneNumber - dateTwoNumber;
};

/**
 * Time setter and return epoch value.
 * @param {string} date - Date
 * @param {string} hour - hour
 * @param {string} minutes - Date
 * @param {string} seconds - seconds
 * @param {string} milliseconds - milliseconds
 * @returns {number} Date
 */
export const dateTimeSetter = (date, hour = "05", minutes = "30", seconds = "00", milliseconds = "00") => {
    return moment(new Date(date))
        .set({
            "hour": hour,
            "millisecond": milliseconds,
            "minute": minutes,
            "second": seconds
        })
        .valueOf();
};

/**
 * This method provides time string to show in extra details
 * @param {string} time - time
 * @returns {string} in hour/minutes/seconds
 */
export const getTimeInFormat = (time) => {
    if (time) {
        const formatterTime = moment(time, "h:mm:ss");
        const hour = formatterTime.hour();
        const minute = formatterTime.minute();
        const second = formatterTime.second();
        if (hour) {
            return `${hour} ${hour > 1 ? "h" : "h"}`;
        } else if (minute) {
            return `${minute} ${minute > 1 ? "m" : "m"}`;
        } else if (second) {
            return `${second} ${second > 1 ? "s" : "s"}`;
        }

        return time;
    }

    return "";
};

/**
 *
 */
export const dateFilterComparator = (a, b) => {
    const dateA = moment(a, "MMMM DD, YYYY [at] hh:mm A", true);
    const dateB = moment(b, "MMMM DD, YYYY [at] hh:mm A", true);

    return dateA.valueOf() - dateB.valueOf();
};

/**
 *
 */
export const filterValueGetterLastActivity = (params) => {
    return moment(Number(params.data.last_activity_on)).format("MMMM DD, YYYY [at] hh:mm A");
};

/**
 *
 */
export const filterValueGetterAddedOn = (params) => {
    return moment(Number(params.data.added_on)).format("MMMM DD, YYYY [at] hh:mm A");
};
